import React, { useRef, useState } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { OrganizationPreferenceResponseBody } from '@rtkwlf/notification-service';
import { faInfoCircle } from '@rtkwlf/pro-solid-svg-icons/faInfoCircle';
import { useTranslation } from 'react-i18next';

import {
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormLabel,
} from '@packages/fenrir/components';

import ItsmPopover from '../itsm-popover/itsm-popover';

interface DeliveryMethodGroupProps {
  preferences?: OrganizationPreferenceResponseBody;
  onEmailChange: (isEmail: boolean) => void;
  onItsmChange: (isITSM: boolean) => void;
  isUpdating?: boolean;
}

const DeliveryMethodGroup = ({
  preferences,
  onEmailChange,
  onItsmChange,
  isUpdating,
}: DeliveryMethodGroupProps) => {
  const { t } = useTranslation('notifications');

  const [itsmPopoverOpen, setItsmPopoverOpen] = useState(false);
  const infoIconRef = useRef<HTMLSpanElement | null>(null);
  const [isEmailCheckEnabled, setIsEmailCheckEnabled] = useState(
    !!preferences?.isEmailEnabled,
  );
  const [isItsmCheckEnabled, setIsItsmCheckEnabled] = useState(
    !!preferences?.isITSMEnabled,
  );
  const isItsmIntegrationSyncEnabled =
    preferences?.isITSMIntegrationSyncEnabled ?? false;

  function handleInfoIconClose(): void {
    setItsmPopoverOpen(false);
  }

  function handleInfoIconOpen(): void {
    setItsmPopoverOpen(true);
  }

  function handleEmailChange(event: React.ChangeEvent<HTMLInputElement>): void {
    onEmailChange(event.target.checked);
  }

  function handleItsmChange(event: React.ChangeEvent<HTMLInputElement>): void {
    onItsmChange(event.target.checked);
  }

  return (
    <FormGroup sx={{ mb: '1rem' }}>
      <FormLabel>{t('deliveryMethod')}</FormLabel>
      <FormControlLabel
        sx={{ py: '.125rem' }}
        disabled={isUpdating}
        control={
          <Checkbox
            checked={isEmailCheckEnabled}
            onClick={() => setIsEmailCheckEnabled(!isEmailCheckEnabled)}
            onChange={handleEmailChange}
            disabled={isUpdating}
            sx={{ py: '.125rem' }}
          />
        }
        label={t('deliveryMethods.email')}
      />
      <FormControlLabel
        sx={{ py: '.125rem' }}
        // allow to be checked if integration is active, else unchecked & greyed out
        disabled={!isItsmIntegrationSyncEnabled}
        control={
          <Checkbox
            checked={isItsmCheckEnabled}
            onClick={() => setIsItsmCheckEnabled(!isItsmCheckEnabled)}
            onChange={handleItsmChange}
            disabled={!isItsmIntegrationSyncEnabled || isUpdating}
            sx={{ py: '.125rem' }}
          />
        }
        label={
          <Box display="flex" alignItems="center">
            {t('deliveryMethods.itsm')}
            <Box component="span" marginLeft={1} display="inline-flex">
              <span
                ref={infoIconRef}
                onMouseOver={handleInfoIconOpen}
                style={{ display: 'inline-flex', alignItems: 'center' }}
                tabIndex={0}
                role="button"
                aria-label="Show ITSM info"
              >
                <FontAwesomeIcon
                  icon={faInfoCircle}
                  size="lg"
                  style={{ color: 'gray', cursor: 'pointer' }}
                />
              </span>
              <ItsmPopover
                bodyText={
                  !isItsmIntegrationSyncEnabled && isItsmCheckEnabled
                    ? t('itsmPopover.itsmSyncDisabledText')
                    : t('itsmPopover.text')
                }
                linkText={
                  !isItsmIntegrationSyncEnabled && isItsmCheckEnabled
                    ? ''
                    : t('itsmPopover.linkText')
                }
                anchorEl={infoIconRef.current}
                open={itsmPopoverOpen}
                onClose={handleInfoIconClose}
              />
            </Box>
          </Box>
        }
      />
    </FormGroup>
  );
};

export default DeliveryMethodGroup;
